import React, {
    ForwardedRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';

import { Platform, View } from 'react-native';

import 'bitmovin-player/bitmovinplayer-ui.css';
import {
    HttpRequestType,
    Player,
    PlayerAPI,
    PlayerConfig,
    PlayerEvent,
    SourceConfigOptions,
} from 'bitmovin-player/modules/bitmovinplayer-core';

import useSsmSession from 'services/ssm/hooks/useSsmSession.ts';

import debugLog from 'utils/debugLog.ts';
import { isSafari } from 'utils/responsiveness.ts';

import {
    BITMOVIN_LICENSE_KEY,
    DEFAULT_SOURCE_LOADING_ERROR_CODE,
    PLAYER_ELEMENT_ID,
} from './constants.ts';
import { getBitmovinDrmConfig } from './drm.web.ts';
import useAudioSubtitlesChange from './hooks/useAudioSubtitlesChange/index.web.ts';
import useCustomUI from './hooks/useCustomUI/index.web.ts';
import useDebouncedPlayerSource from './hooks/useDebouncedPlayerSource';
import useMuted from './hooks/useMuted/index.web.ts';
import useStatisticsUpdate from './hooks/useStatisticsUpdate';
import useTimeChangedEvent from './hooks/useTimeChangedEvent/index.web.ts';
import useVolume from './hooks/useVolume/index.web.ts';
import {
    BitmovinPlayerError,
    BitmovinPlayerSource,
    PlayerProps,
    PlayerRef,
} from './types.ts';
import registerModules from './web/registerModules.ts';
import useEventHandler from './web/useEventHandler.ts';

function BitmovinWebPlayer(
    {
        muted,
        volume,
        style,
        source,
        webSubtitleFontSize,
        statisticsUpdateInterval,
        onPlayerSourceLoaded,
        onReady,
        onProgress,
        onWaiting,
        onPaused,
        onPlaying,
        onSeeked,
        onEnd,
        onError,
        onTracksChanged,
        onStatisticsUpdate,
    }: PlayerProps,
    playerRef: ForwardedRef<PlayerRef>,
) {
    const ssm = useSsmSession(onError);

    const playerElement = useRef<HTMLElement | null>(null);
    const mounted = useRef(false);

    const [player, setPlayer] = useState<PlayerAPI | null>(null);
    const [playing, setPlaying] = useState(false);
    const [playerSourceLoading, setPlayerSourceLoading] = useState(false);

    const createCustomUI = useCustomUI();
    const onTimeChangedEvent = useTimeChangedEvent(player, onProgress);
    const { handleAudioSubtitlesChange } = useAudioSubtitlesChange({
        player,
        onTracksChanged,
    });

    const playerConfig: PlayerConfig = {
        key: BITMOVIN_LICENSE_KEY,
        ui: false,
        playback: {
            autoplay: true,
        },
        tweaks: {
            native_hls_parsing: true,
        },
        network: {
            preprocessHttpRequest: async (type, request) => {
                if (type === HttpRequestType.DRM_LICENSE_FAIRPLAY) {
                    return ssm.onDrmLicenseFairplayRequest(request);
                }

                if (type === HttpRequestType.DRM_LICENSE_WIDEVINE) {
                    return ssm.onDrmLicenseWidevineRequest(request);
                }

                return Promise.resolve(request);
            },
            preprocessHttpResponse: (type, response) => {
                if (type === HttpRequestType.DRM_LICENSE_FAIRPLAY) {
                    return ssm.onDrmLicenseFairplayResponse(response);
                }

                if (type === HttpRequestType.DRM_LICENSE_WIDEVINE) {
                    return ssm.onDrmLicenseWidevineResponse(response);
                }

                if (
                    type === HttpRequestType.MANIFEST_HLS_MASTER &&
                    response.body
                ) {
                    return Promise.resolve({
                        ...response,
                        // @ts-ignore
                        body: response.body.replace(
                            /,CHARACTERISTICS="public.accessibility.transcribes-spoken-dialog,public.accessibility.describes-music-and-sound"/,
                            '',
                        ),
                    });
                }

                return Promise.resolve(response);
            },
        },
    };

    useImperativeHandle(
        playerRef,
        () => ({
            play: () => {
                debugLog(`BITMOVIN playerRef.play()`);
                player?.play();
            },
            pause: () => {
                debugLog(`BITMOVIN playerRef.pause()`);
                player?.pause();
            },
            seek: (value: number) => {
                debugLog(`BITMOVIN playerRef.seek(${value})`, player?.isLive());
                player?.seek(player?.getSeekableRange().start + value);
            },
            unload: async () => {
                debugLog(`BITMOVIN playerRef.unload()`);
                setPlaying(false);
                player?.unload();
                await ssm.teardown();
            },
            timeShift: (offset: number) => {
                debugLog(
                    `BITMOVIN playerRef.timeShift(${offset})`,
                    player?.isLive(),
                );
                player?.timeShift(offset);
            },
            getTimeShift: async () => {
                // debugLog(`BITMOVIN playerRef.getTimeShift()`);
                return player?.getTimeShift();
            },
            getMaxTimeShift: async () => {
                // debugLog(`BITMOVIN playerRef.getMaxTimeShift()`);
                return player?.getMaxTimeShift();
            },
            getDuration: async () => {
                // debugLog(`BITMOVIN playerRef.getDuration()`);
                return player?.getDuration();
            },
            getIsLive: async () => {
                // debugLog(`BITMOVIN playerRef.getIsLive()`);
                return player?.isLive();
            },
            selectAudioTrack: async (trackId: string) => {
                player?.setAudio(trackId);
            },
            selectTextTrack: async (trackId: string | undefined) => {
                if (trackId) {
                    player?.subtitles.enable(trackId);
                } else {
                    const subtitleTracks = player?.subtitles.list();
                    const selectedTrack = subtitleTracks?.find(
                        ({ enabled }) => enabled,
                    );

                    if (selectedTrack) {
                        player?.subtitles.disable(selectedTrack.id);
                    }
                }
            },
        }),
        [player],
    );

    const handleSourceLoadedEvent = useCallback(
        (e) => {
            debugLog(`BITMOVIN ${PlayerEvent.SourceLoaded} event`, e);

            onPlayerSourceLoaded?.();
        },
        [onPlayerSourceLoaded],
    );

    const handleReadyEvent = useCallback(
        (event) => {
            debugLog(`BITMOVIN ${PlayerEvent.Ready}`, event);

            if (ssm.contentToken) {
                ssm.licenseAdded = true;
            }

            handleAudioSubtitlesChange();
            onReady({ duration: player?.getDuration() });
        },
        [handleAudioSubtitlesChange, onReady, player, ssm],
    );

    const handleTimeshiftEvent = useCallback((event) => {
        debugLog(`BITMOVIN ${PlayerEvent.TimeShift}`, event);
    }, []);

    const handleTimeshiftedEvent = useCallback(
        (event) => {
            debugLog(`BITMOVIN ${PlayerEvent.TimeShifted}`, event);
            onSeeked();
        },
        [onSeeked],
    );

    const handleSeekedEvent = useCallback(
        (event) => {
            debugLog(`BITMOVIN ${PlayerEvent.Seeked}`, event);
            onSeeked();
        },
        [onSeeked],
    );

    const handleStallStartedEvent = useCallback(() => {
        setPlaying(false);
        onWaiting?.();
    }, [onWaiting]);

    const handleStallEndedEvent = useCallback(() => {
        setPlaying(true);
        onPlaying?.();
    }, [onPlaying]);

    const handlePausedEvent = useCallback(() => {
        setPlaying(false);
        onPaused?.();
    }, [onPaused]);

    const handlePlayingEvent = useCallback(() => {
        setPlaying(true);
        onPlaying?.();
    }, [onPlaying]);

    const handlePlaybackFinishedEvent = useCallback(() => {
        setPlaying(false);
        onEnd?.();
    }, [onEnd]);

    const handleWarningEvent = useCallback((event) => {
        debugLog(`BITMOVIN ${PlayerEvent.Warning}`, event);
    }, []);

    const handleErrorEvent = useCallback(
        (event) => {
            debugLog(`BITMOVIN ${PlayerEvent.Error}`, event);
            setPlaying(false);
            onError?.(event);
        },
        [onError],
    );

    const loadPlayerSource = async (_source: BitmovinPlayerSource) => {
        try {
            if (_source && playerElement.current) {
                setPlayerSourceLoading(true);
                debugLog('BITMOVIN', 'new source', _source);

                registerModules();

                let _player = player;

                if (!_player) {
                    _player = new Player(playerElement.current, playerConfig);
                } else {
                    await _player.unload();
                }

                if (_source.onGetContentToken) {
                    await ssm.setup(_source.onGetContentToken);
                }

                const playerSource = {
                    ...Platform.select({
                        ios: { hls: _source.src },
                        android: { dash: _source.src },
                        web: isSafari
                            ? { hls: _source.src }
                            : { dash: _source.src },
                    }),
                    options: _source.sourceConfig as SourceConfigOptions,
                    drm: getBitmovinDrmConfig(ssm.contentToken),
                };

                createCustomUI(_player, {
                    subtitleFontSize: webSubtitleFontSize,
                });

                if (mounted.current) {
                    await _player.load(playerSource);
                    setPlayer(_player);
                    debugLog('BITMOVIN', 'source load finished', playerSource);
                } else {
                    await _player.unload();
                    await _player.destroy();
                    await ssm.teardown();
                    debugLog(
                        'BITMOVIN',
                        'source load interrupted by component unmount',
                        playerSource,
                    );
                }

                setPlayerSourceLoading(false);
            } else if (player && !_source) {
                await player.unload();
                await ssm.teardown();
                setPlayerSourceLoading(false);
            }
        } catch (error) {
            const bitmovinError = error as BitmovinPlayerError;

            debugLog('BITMOVIN', 'Source Load Error', bitmovinError);

            onError({
                code: bitmovinError?.code ?? DEFAULT_SOURCE_LOADING_ERROR_CODE,
                data: bitmovinError,
            });

            await player?.unload();
            await ssm.teardown();

            setPlayerSourceLoading(false);
        }
    };

    useDebouncedPlayerSource(source, playerSourceLoading, loadPlayerSource);

    useVolume(player, volume);
    useMuted(player, muted);
    useStatisticsUpdate(
        player,
        playing,
        onStatisticsUpdate,
        statisticsUpdateInterval,
    );

    useEventHandler(player, PlayerEvent.SourceLoaded, handleSourceLoadedEvent);
    useEventHandler(player, PlayerEvent.Ready, handleReadyEvent);
    useEventHandler(player, PlayerEvent.TimeChanged, onTimeChangedEvent);
    useEventHandler(player, PlayerEvent.Seeked, handleSeekedEvent);
    useEventHandler(player, PlayerEvent.TimeShift, handleTimeshiftEvent);
    useEventHandler(player, PlayerEvent.TimeShifted, handleTimeshiftedEvent);
    useEventHandler(player, PlayerEvent.StallStarted, handleStallStartedEvent);
    useEventHandler(player, PlayerEvent.StallEnded, handleStallEndedEvent);
    useEventHandler(player, PlayerEvent.Paused, handlePausedEvent);
    useEventHandler(player, PlayerEvent.Playing, handlePlayingEvent);
    useEventHandler(
        player,
        PlayerEvent.PlaybackFinished,
        handlePlaybackFinishedEvent,
    );
    useEventHandler(player, PlayerEvent.Warning, handleWarningEvent);
    useEventHandler(player, PlayerEvent.Error, handleErrorEvent);
    useEventHandler(player, PlayerEvent.AudioAdded, handleAudioSubtitlesChange);
    useEventHandler(
        player,
        PlayerEvent.AudioRemoved,
        handleAudioSubtitlesChange,
    );
    useEventHandler(
        player,
        PlayerEvent.SubtitleAdded,
        handleAudioSubtitlesChange,
    );
    useEventHandler(
        player,
        PlayerEvent.SubtitleRemoved,
        handleAudioSubtitlesChange,
    );

    useEffect(
        () => () => {
            if (player) {
                player.destroy();
                ssm.teardown();
                setPlaying(false);
                setPlayer(null);
            }
        },
        [player],
    );

    useLayoutEffect(() => {
        mounted.current = true;

        return () => {
            mounted.current = false;
        };
    }, []);

    return (
        <View
            // @ts-ignore
            ref={playerElement}
            id={PLAYER_ELEMENT_ID}
            style={style}
        />
    );
}

export default React.forwardRef(BitmovinWebPlayer);
